import React, { useMemo } from "react";
import { Card, Table, Typography, Empty, Spin, Button, Space, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useGetLevelDParticipantCompetencyPoints, useExportParticipantCompetencyPoints, useExportParticipantQuestionDetails } from "../../hooks/leveld-reports";
import { LevelDReportFilter } from "../../types/leveld-reports";

interface LevelDParticipantCompetencyTableProps {
  filter: LevelDReportFilter;
}

interface TableRow {
  key: string;
  participantName: string;
  topCategory: string;
  competencyName: string;
  essayPoints: number;
  multipleChoicePoints: number;
  isFirstRowForParticipant: boolean;
  isFirstRowForTopCategory: boolean;
  participantRowSpan: number;
  topCategoryRowSpan: number;
}

export const LevelDParticipantCompetencyTable: React.FC<LevelDParticipantCompetencyTableProps> = ({
  filter,
}) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetLevelDParticipantCompetencyPoints({
    filter,
    enabled: Boolean(filter.examTermId),
  });
  const { exportToExcel, isExporting } = useExportParticipantCompetencyPoints();
  const { exportToExcel: exportQuestionDetails, isExporting: isExportingQuestionDetails } = useExportParticipantQuestionDetails();

  // Transform data into flat table rows with row span information
  const tableData = useMemo<TableRow[]>(() => {
    if (!data?.participants) return [];

    const rows: TableRow[] = [];

    for (const participant of data.participants) {
      // Group competencies by top category
      const competencyByTopCategory = new Map<string, typeof participant.competencyPoints>();
      
      for (const cp of participant.competencyPoints) {
        if (!competencyByTopCategory.has(cp.topCategory)) {
          competencyByTopCategory.set(cp.topCategory, []);
        }
        competencyByTopCategory.get(cp.topCategory)!.push(cp);
      }

      const sortedTopCategories = Array.from(competencyByTopCategory.keys()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      const participantTotalRows = participant.competencyPoints.length;
      let isFirstRowForParticipant = true;

      for (const topCategory of sortedTopCategories) {
        const competencies = competencyByTopCategory.get(topCategory)!;
        const sortedCompetencies = competencies.sort((a, b) => 
          a.competencyName.localeCompare(b.competencyName, undefined, { numeric: true }));
        const topCategoryRowCount = sortedCompetencies.length;
        let isFirstRowForTopCategory = true;

        for (const cp of sortedCompetencies) {
          rows.push({
            key: `${participant.examInstanceId}-${cp.competencyName}`,
            participantName: participant.participantName,
            topCategory: cp.topCategory,
            competencyName: cp.competencyName,
            essayPoints: cp.essayPoints,
            multipleChoicePoints: cp.multipleChoicePoints,
            isFirstRowForParticipant,
            isFirstRowForTopCategory,
            participantRowSpan: isFirstRowForParticipant ? participantTotalRows : 0,
            topCategoryRowSpan: isFirstRowForTopCategory ? topCategoryRowCount : 0,
          });

          isFirstRowForParticipant = false;
          isFirstRowForTopCategory = false;
        }
      }
    }

    return rows;
  }, [data]);

  const columns = useMemo<ColumnsType<TableRow>>(() => [
    {
      title: t("levelDReports.participantPoints.examinee"),
      dataIndex: "participantName",
      key: "participantName",
      fixed: "left",
      width: 180,
      onCell: (record) => ({
        rowSpan: record.participantRowSpan,
      }),
      render: (name: string) => (
        <Typography.Text strong>{name}</Typography.Text>
      ),
    },
    {
      title: t("levelDReports.participantPoints.topCategory"),
      dataIndex: "topCategory",
      key: "topCategory",
      width: 100,
      onCell: (record) => ({
        rowSpan: record.topCategoryRowSpan,
      }),
      render: (topCategory: string) => (
        <Typography.Text>{topCategory}</Typography.Text>
      ),
    },
    {
      title: t("levelDReports.participantPoints.competency"),
      dataIndex: "competencyName",
      key: "competencyName",
      width: 120,
      render: (competencyName: string) => (
        <Typography.Text>{competencyName}</Typography.Text>
      ),
    },
    {
      title: t("levelDReports.participantPoints.essay"),
      dataIndex: "essayPoints",
      key: "essayPoints",
      width: 80,
      align: "center",
      render: (points: number) => (
        <Typography.Text>{points}</Typography.Text>
      ),
    },
    {
      title: t("levelDReports.participantPoints.multipleChoice"),
      dataIndex: "multipleChoicePoints",
      key: "multipleChoicePoints",
      width: 100,
      align: "center",
      render: (points: number) => (
        <Typography.Text>{points}</Typography.Text>
      ),
    },
  ], [t]);

  if (!filter.examTermId) {
    return (
      <Card 
        title={
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t("levelDReports.participantPoints.title")}
          </Typography.Title>
        }
        style={{ marginBottom: 24 }}
      >
        <Empty 
          description={t("levelDReports.participantPoints.selectTerm")} 
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card 
        title={
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t("levelDReports.participantPoints.title")}
          </Typography.Title>
        }
        style={{ marginBottom: 24 }}
      >
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!data?.participants || data.participants.length === 0) {
    return (
      <Card 
        title={
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t("levelDReports.participantPoints.title")}
          </Typography.Title>
        }
        style={{ marginBottom: 24 }}
      >
        <Empty description={t("levelDReports.participantPoints.noData")} />
      </Card>
    );
  }

  return (
    <Card 
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t("levelDReports.participantPoints.title")}
        </Typography.Title>
      }
      extra={
        <Space>
          <Typography.Text type="secondary">
            {t("levelDReports.participantPoints.description", { 
              count: data.participants.length 
            })}
          </Typography.Text>
          <Tooltip title={t("levelDReports.participantPoints.exportTooltip")}>
            <Button
              type="primary"
              size="small"
              icon={<DownloadOutlined />}
              loading={isExporting}
              onClick={() => exportToExcel(filter)}
            >
              {t("levelDReports.participantPoints.exportButton")}
            </Button>
          </Tooltip>
          <Tooltip title={t("levelDReports.questionDetails.exportTooltip")}>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={isExportingQuestionDetails}
              onClick={() => exportQuestionDetails(filter)}
            >
              {t("levelDReports.questionDetails.exportButton")}
            </Button>
          </Tooltip>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        scroll={{ x: 600, y: 600 }}
        size="small"
        bordered
        rowKey="key"
      />
    </Card>
  );
};
